/**
 * Connect Auth API
 * Implements the provider auth flow from the doc:
 * 1. GET /api/v0/users/details — validate token
 * 2. GET /api/v0/auth/providers/users — check provider assignment
 * 3. GET /api/v0/auth/providers/{id}?methods=navbar_config,children_details — get provider data
 * 4. GET /leads/agents/current — validate agent role (admin access)
 */

import { api } from "@/lib/api";
import type {
	AgentResponse,
	ProviderDetailsResponse,
	ProviderUsersResponse,
} from "./types";

/**
 * Step 1: Validate user session
 * Equivalent to auth.isAuthenticated middleware
 */
export async function validateUserSession(): Promise<{
	id: number;
	name: string;
	email: string;
}> {
	const response = await api.get<{
		data: { id: number; name: string; email: string };
	}>("/api/v0/users/details");
	return response.data.data;
}

/**
 * Step 2: Check if user is assigned to a provider
 * Equivalent to auth.isProvider middleware (normal flow)
 */
export async function getProviderUsers(): Promise<ProviderUsersResponse> {
	const response = await api.get<ProviderUsersResponse>(
		"/api/v0/auth/providers/users?cache=false",
	);
	return response.data;
}

/**
 * Step 3: Get provider details with navbar_config
 * Equivalent to handleProviderRoutes
 */
export async function getProviderDetails(
	providerId: number,
): Promise<ProviderDetailsResponse> {
	const response = await api.get<ProviderDetailsResponse>(
		`/api/v0/auth/providers/${providerId}?methods=navbar_config,children_details`,
	);
	return response.data;
}

/**
 * Step 4: Validate agent role (admin access via ?provider_id=)
 * Equivalent to auth.checkAccountRole
 */
export async function validateAgentRole(): Promise<AgentResponse> {
	const response = await api.get<AgentResponse>(
		"/leads/agents/current?methods=children_ids,resources&include=phones",
	);
	return response.data;
}

/**
 * Logout
 */
export async function logout(): Promise<void> {
	await api.post("/api/v0/users/logout");
}
